/**
 * VÉRAULT Portal — Cross-Experience Agreements Index (PEOPLE group).
 *
 * Top-level operational view of every agreement across every Experience.
 * The source of truth still lives inside each Experience detail; this
 * page is a calm, filterable index for advisors who want to scan the
 * full pipeline at once (e.g. "what's drafted but not yet sent",
 * "what's been sent but is sitting unsigned").
 *
 * Powered by GET /api/agreements (cross-trip aggregator with hydrated
 * trip + client labels).
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, AlertCircle, Search } from "lucide-react";

import {
  Surface,
  EditorialRule,
  StatusTag,
  PageHeader,
  PortalBreadcrumb,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "drafted", label: "Drafted", match: (a) => a.status === "drafted" },
  { id: "sent", label: "Sent", match: (a) => a.status === "sent" },
  { id: "acknowledged", label: "Signed", match: (a) => a.status === "acknowledged" },
  { id: "declined", label: "Declined / Expired", match: (a) => ["declined", "expired"].includes(a.status) },
];

function statusTone(s) {
  if (s === "acknowledged") return "sage";
  if (s === "sent") return "gold";
  if (s === "declined" || s === "expired") return "terracotta";
  return "neutral";
}

function humanType(t) {
  if (!t) return "—";
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AdvisorAgreements() {
  const [items, setItems] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState("all");
  const [q, setQ] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    api
      .get("/agreements")
      .then(({ data }) => alive && setItems(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load agreements."));
    return () => {
      alive = false;
    };
  }, []);

  const counts = React.useMemo(() => {
    const c = { all: 0, drafted: 0, sent: 0, acknowledged: 0, declined: 0 };
    (items || []).forEach((a) => {
      c.all += 1;
      if (a.status === "declined" || a.status === "expired") c.declined += 1;
      else if (c[a.status] != null) c[a.status] += 1;
    });
    return c;
  }, [items]);

  const filtered = React.useMemo(() => {
    if (!items) return null;
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    let result = items.filter(f.match);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      result = result.filter((a) =>
        [a.title, a.trip_label, a.client_name, a.type]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(needle))
      );
    }
    return result;
  }, [items, filter, q]);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PortalBreadcrumb
          parent={{ to: "/portal/advisor", label: "Studio" }}
          trail={[{ label: "Agreements" }]}
          exitTo="/"
          exitLabel="Exit to website"
        />
        <PageHeader
          eyebrow="People · Agreements"
          title="Every agreement, every Experience."
          lede="A cross-Experience index. Open one to jump straight into its Journey."
        />
      </motion.div>

      <motion.div variants={fadeInUp} className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
        <nav className="flex flex-wrap gap-1" data-testid="agreements-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`agreements-filter-${f.id}`}
              className={`group relative px-3 py-2 font-body text-[13px] tracking-tight transition-colors duration-200 ${
                filter === f.id
                  ? "text-[hsl(var(--foreground))]"
                  : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <span className={filter === f.id ? "font-serif" : ""}>{f.label}</span>
              {counts[f.id] > 0 && (
                <span className="ml-1.5 font-mono text-[10px] text-[hsl(var(--foreground-muted))]">
                  {counts[f.id]}
                </span>
              )}
              <span
                className={`absolute -bottom-px left-3 right-3 h-px transition-colors duration-300 ${
                  filter === f.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                }`}
                aria-hidden
              />
            </button>
          ))}
        </nav>

        <div className="relative">
          <Search className="absolute left-3 top-2.5 size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, trip, client…"
            data-testid="agreements-search"
            className="pl-9 pr-3 py-2 text-[13px] bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] w-72"
          />
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label={filtered ? `${filtered.length} agreement${filtered.length === 1 ? "" : "s"}` : "loading"} />
        <div className="mt-5">
          {error ? (
            <Empty title={error} icon={<AlertCircle className="size-6" strokeWidth={1.25} />} />
          ) : !filtered ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Empty
              title="Nothing in this view"
              hint="Agreements are created from inside each Experience detail's Agreements tab."
              icon={<BookOpen className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]" data-testid="agreements-rows">
                {filtered.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/portal/advisor/journeys/${a.trip_id}?tab=agreements`}
                      className="group grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_180px_140px_auto] items-center gap-3 sm:gap-6 px-5 sm:px-6 py-4 hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
                      data-testid={`agreement-row-${a.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-serif text-lg text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors truncate">
                          {a.title || humanType(a.type)}
                        </p>
                        <p className="mt-0.5 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] truncate">
                          {[a.client_name, a.trip_label].filter(Boolean).join(" · ") || "—"}
                        </p>
                      </div>
                      <div className="hidden sm:block font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                        {humanType(a.type)}
                        {a.trip_start ? (
                          <span className="block mt-0.5">{fmt.formatDate(a.trip_start)}</span>
                        ) : null}
                      </div>
                      <div className="hidden sm:block">
                        <StatusTag tone={statusTone(a.status)}>{a.status === "acknowledged" ? "signed" : a.status}</StatusTag>
                      </div>
                      <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                        {a.updated_at ? fmt.formatDate(a.updated_at) : "—"}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
